import * as React from "react";
import warning from "tiny-warning";

import { RouterProps, Route, ScreenTransitionMode } from "@core-ui/interfaces/Router";
import { RouteContext, RouteContextValue, NavigateToOptions } from "@core-ui/contexts/RouteContext";
import { RouteStack, findRouteByName, buildRoutes, getRouteElement, addToStack } from "./Utils";
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";
import { ScreenTransition } from "@core-ui/components/screen-transition";

export function Router<T extends string = string>(props: RouterProps<T>): React.ReactElement | null {
    const {
        children,
        externalRoutes = [],
        nonOverlappingTransitionRoutes = [],
        preservePendingTransitionOnSiblingNav = false,
        ignoreBubbledTransitionEnd = false,
    } = props;
    const routes = React.useMemo(() => buildRoutes([...children, ...externalRoutes]), [children, externalRoutes]);

    const [routeState, setRouteState] = React.useState<{
        forward: boolean;
        activeRoute: Route<T> | undefined;
        routeStack: RouteStack<T>;
    }>(() => {
        const route = findRouteByName(routes, props.initialRoute.name);
        const activeRoute = route ? { ...route, data: props.initialRoute.data } : route;

        return {
            forward: true,
            activeRoute,
            routeStack: activeRoute ? [{ name: activeRoute.name, data: activeRoute.data }] : [],
        };
    });

    const transitionMode = React.useMemo<ScreenTransitionMode>(() => {
        const isNonOverlapping =
            !!routeState.activeRoute?.name && nonOverlappingTransitionRoutes.includes(routeState.activeRoute.name);
        return isNonOverlapping ? "out-in" : "in-out";
    }, [routeState.activeRoute?.name, nonOverlappingTransitionRoutes]);

    const canGoBack = React.useCallback(
        (numberOfPagesToGoBack = 1): boolean => {
            return routeState?.routeStack.length > numberOfPagesToGoBack;
        },
        [routeState],
    );

    const pendingTransitionRef = React.useRef<
        { routeName: T; onEnter?: VoidFunction; onExit?: VoidFunction } | undefined
    >();

    // Enter and exit callbacks are flushed independently so a nav that registers both does not
    // lose one to whichever phase completes first (`in-out` enters first, `out-in` exits first).
    const flushPendingTransition = React.useCallback((phase: "onEnter" | "onExit") => {
        const pendingTransition = pendingTransitionRef.current;
        const callback = pendingTransition?.[phase];

        if (!pendingTransition || !callback) {
            return;
        }

        const otherPhaseCallback = phase === "onEnter" ? pendingTransition.onExit : pendingTransition.onEnter;
        pendingTransitionRef.current = otherPhaseCallback
            ? { ...pendingTransition, [phase]: undefined }
            : undefined;

        callback();
    }, []);

    const notifyTransitionComplete = React.useCallback(() => {
        flushPendingTransition("onEnter");
    }, [flushPendingTransition]);

    const notifyTransitionExit = React.useCallback(() => {
        flushPendingTransition("onExit");
    }, [flushPendingTransition]);

    const getScreenTransitionKey = React.useCallback(
        (route?: Route<T>) => {
            const element = getRouteElement(routes, route);
            return element?.key ?? route?.name ?? "no_key";
        },
        [routes],
    );

    const navigateTo = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (routeName: T, data?: any, replace?: boolean, errorCallbackOrOptions?: VoidFunction | NavigateToOptions) => {
            let options: NavigateToOptions = {};
            if (typeof errorCallbackOrOptions === "function") {
                options = { errorCallback: errorCallbackOrOptions };
            } else if (errorCallbackOrOptions) {
                options = errorCallbackOrOptions;
            }

            const route = findRouteByName(routes, routeName, routeState.activeRoute?.name);
            if (!route) {
                warning(false, `invalid route ${routeName} specified`);
                pendingTransitionRef.current = undefined;
                options.errorCallback?.();
                return;
            }

            const [stack, forward] = addToStack({
                replace,
                data,
                name: routeName,
                stack: routeState.routeStack,
            });

            const nextActiveRoute = { ...route, data };

            if (options.onTransitionComplete || options.onTransitionExit) {
                pendingTransitionRef.current = {
                    routeName,
                    onEnter: options.onTransitionComplete,
                    onExit: options.onTransitionExit,
                };
            } else if (pendingTransitionRef.current && pendingTransitionRef.current.routeName !== routeName) {
                // A duplicate nav to the same route (RouteContainer state sync racing an explicit
                // nav) shares the in-flight transition, so it must never drop the pending callback.
                // Beyond that, only a same-outer-key hop retains it, and only for QC/AIP where the
                // nested shell ScreenTransition owns completion.
                const retainForNestedShell =
                    preservePendingTransitionOnSiblingNav &&
                    getScreenTransitionKey(routeState.activeRoute) === getScreenTransitionKey(nextActiveRoute);

                if (!retainForNestedShell) {
                    pendingTransitionRef.current = undefined;
                }
            }

            setRouteState({
                forward,
                activeRoute: nextActiveRoute,
                routeStack: stack,
            });
        },
        [
            routes,
            routeState.activeRoute,
            routeState.routeStack,
            getScreenTransitionKey,
            preservePendingTransitionOnSiblingNav,
        ],
    );

    const goBack = React.useCallback((): void => {
        if (!canGoBack()) {
            return;
        }

        const entry = routeState.routeStack[routeState.routeStack.length - 2];
        if (!entry) {
            return;
        }

        navigateTo(entry.name, entry.data);
    }, [canGoBack, navigateTo, routeState?.routeStack]);

    const contextValue = React.useMemo<RouteContextValue<T>>(
        () => ({
            navigateTo,
            goBack,
            canGoBack,
            route: routeState.activeRoute,
            isForward: routeState.forward,
            notifyTransitionComplete,
            notifyTransitionExit,
            transitionMode,
        }),
        [
            canGoBack,
            goBack,
            navigateTo,
            notifyTransitionComplete,
            notifyTransitionExit,
            routeState.activeRoute,
            routeState.forward,
            transitionMode,
        ],
    ) as unknown;

    useEffectOnce(() => {
        history.pushState(
            { name: props.initialRoute.name ?? "show-payments", data: props.initialRoute.data ?? undefined },
            "",
            "",
        );
    });

    const Container = props.container || React.Fragment;

    return (
        <RouteContext.Provider value={contextValue as RouteContextValue<string>}>
            <Container>
                <ScreenTransition
                    transitionMode={transitionMode}
                    forward={routeState.forward}
                    onEntered={notifyTransitionComplete}
                    onExited={notifyTransitionExit}
                    ignoreBubbledTransitionEnd={ignoreBubbledTransitionEnd}
                >
                    {getRouteElement(routes, routeState.activeRoute) ?? <div key="no-matching-element"></div>}
                </ScreenTransition>
            </Container>
        </RouteContext.Provider>
    );
}
